import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { useOfflineQueue } from '@/utils/offlineQueue';
import { useAppDispatch } from '@/store';
import { clearErrors } from '@/store/slices/errorSlice';

interface ErrorRecoveryDialogProps {
  open: boolean;
  onClose: () => void;
  error?: {
    message: string;
    code?: string;
    canRetry?: boolean;
  };
  onRetry?: () => void;
}

export const ErrorRecoveryDialog: React.FC<ErrorRecoveryDialogProps> = ({
  open,
  onClose,
  error,
  onRetry,
}) => {
  const dispatch = useAppDispatch();
  const handleClose = () => {
    dispatch(clearErrors());
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ErrorOutlineIcon color="error" />
          <Typography variant="h6">Error Occurred</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.message || 'An unexpected error occurred'}
        </Alert>
        {error?.code && (
          <Typography variant="caption" color="text.secondary">
            Error Code: {error.code}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        {error?.canRetry && onRetry && (
          <Button
            onClick={() => {
              onRetry();
              handleClose();
            }}
            variant="contained"
            startIcon={<RefreshIcon />}
          >
            Retry
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

interface OfflineIndicatorProps {
  isOnline: boolean;
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({ isOnline }) => {
  if (isOnline) return null;

  return (
    <Chip
      icon={<WifiOffIcon />}
      label="Offline"
      color="warning"
      size="small"
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 9999,
      }}
    />
  );
};

export const OfflineQueueIndicator: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const { getQueuedMutations, removeFromQueue, clearQueue } = useOfflineQueue();
  const queuedMutations = getQueuedMutations();

  if (queuedMutations.length === 0) return null;

  return (
    <>
      <Chip
        icon={<CloudOffIcon />}
        label={`${queuedMutations.length} pending`}
        color="info"
        size="small"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 100,
          zIndex: 9999,
          cursor: 'pointer',
        }}
      />
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Pending Actions</Typography>
            <Button
              size="small"
              color="error"
              onClick={() => {
                clearQueue();
                setOpen(false);
              }}
            >
              Clear All
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            These actions will be retried when connection is restored.
          </Typography>
          <List>
            {queuedMutations.map((mutation) => (
              <ListItem key={mutation.id}>
                <ListItemText
                  primary={mutation.endpoint}
                  secondary={`Queued ${new Date(mutation.timestamp).toLocaleString()}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => removeFromQueue(mutation.id)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

interface NetworkStatusProviderProps {
  children: React.ReactNode;
}

export const NetworkStatusProvider: React.FC<NetworkStatusProviderProps> = ({
  children,
}) => {
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);

  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <>
      {children}
      <OfflineIndicator isOnline={isOnline} />
      <OfflineQueueIndicator />
    </>
  );
};

// Hook for showing error recovery UI
export function useErrorRecovery() {
  const [error, setError] = React.useState<{
    message: string;
    code?: string;
    canRetry?: boolean;
  } | null>(null);
  const [open, setOpen] = React.useState(false);

  const showError = React.useCallback(
    (errorData: { message: string; code?: string; canRetry?: boolean }) => {
      setError(errorData);
      setOpen(true);
    },
    []
  );

  const hideError = React.useCallback(() => {
    setOpen(false);
    setTimeout(() => setError(null), 300);
  }, []);

  const ErrorDialog = React.useCallback(
    ({ onRetry }: { onRetry?: () => void }) => (
      <ErrorRecoveryDialog
        open={open}
        onClose={hideError}
        error={error || undefined}
        onRetry={onRetry}
      />
    ),
    [open, error, hideError]
  );

  return { showError, hideError, ErrorDialog };
}